// src/components/ProjectManagement/KanbanTask.jsx
import React, { useState, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { db } from '../../firebase/config';
import { updateDoc, doc, addDoc, collection, query, where, getDocs, orderBy, deleteDoc } from 'firebase/firestore';
import ExtendTaskDateModal from './ExtendTaskDateModal';

// Edit Task Modal Component
const EditTaskModal = ({ task, onClose, onSave, currentUser }) => {
  const [title, setTitle] = useState(task.title || '');
  const [description, setDescription] = useState(task.description || '');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = 'Task title is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      await updateDoc(doc(db, 'tasks', task.id), {
        title: title.trim(),
        description: description.trim() || '',
        updatedAt: new Date(),
        updatedBy: currentUser.uid,
        updatedByName: currentUser.displayName || currentUser.email?.split('@')[0]
      });

      await addDoc(collection(db, 'taskHistory'), {
        taskId: task.id,
        taskTitle: task.title,
        action: 'task_edited',
        changes: {
          title: { from: task.title, to: title.trim() },
          description: { from: task.description, to: description.trim() }
        },
        performedBy: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email?.split('@')[0],
        timestamp: new Date()
      });

      alert('✅ Task updated successfully!');
      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
        <h3>✏️ Edit Task</h3>
        <p><strong>Task ID:</strong> {task.id.slice(0, 8)}...</p>
        
        <label>Task Title *</label>
        <input 
          type="text" 
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setErrors(prev => ({ ...prev, title: null }));
          }}
          style={{ 
            width: '100%', 
            padding: '10px', 
            borderRadius: '8px', 
            border: `2px solid ${errors.title ? '#ef4444' : '#e5e7eb'}`,
            marginBottom: '16px'
          }}
        />
        {errors.title && <small style={{ color: '#ef4444', marginTop: '-12px', display: 'block', marginBottom: '12px' }}>{errors.title}</small>}
        
        <label>Description</label>
        <textarea 
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="4"
          placeholder="Task description..."
          style={{ 
            width: '100%', 
            padding: '10px', 
            borderRadius: '8px', 
            border: '2px solid #e5e7eb',
            fontFamily: 'inherit',
            marginBottom: '16px'
          }}
        />
        
        <div className="modal-actions" style={{ marginTop: '20px' }}>
          <button onClick={handleSubmit} className="assign-btn" disabled={isLoading}>
            {isLoading ? 'Saving...' : '✓ Save Changes'}
          </button>
          <button onClick={onClose} className="cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteTaskModal = ({ task, onClose, onDelete, currentUser }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const handleDelete = async () => {
    if (confirmText !== task.title) {
      alert('Please type the task title to confirm deletion');
      return;
    }

    setIsLoading(true);
    try {
      // Delete task remarks first
      const remarksQuery = query(collection(db, 'taskRemarks'), where('taskId', '==', task.id));
      const remarksSnapshot = await getDocs(remarksQuery);
      const remarkDeletions = remarksSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(remarkDeletions);

      // Delete task history
      const historyQuery = query(collection(db, 'taskHistory'), where('taskId', '==', task.id));
      const historySnapshot = await getDocs(historyQuery);
      const historyDeletions = historySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(historyDeletions);

      // Add audit log before deletion
      await addDoc(collection(db, 'taskHistory'), {
        taskId: task.id,
        taskTitle: task.title,
        action: 'task_deleted',
        deletedBy: currentUser.uid,
        deletedByName: currentUser.displayName || currentUser.email?.split('@')[0],
        timestamp: new Date()
      });

      // Finally delete the task
      await deleteDoc(doc(db, 'tasks', task.id));

      alert('✅ Task deleted successfully!');
      onDelete?.();
      onClose();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ color: '#dc2626' }}>🗑️ Delete Task</h3>
        <p><strong>Task:</strong> {task.title}</p>
        <p><strong>Project:</strong> {task.projectName}</p>
        <div style={{ 
          background: '#fef2f2', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '16px',
          border: '1px solid #fee2e2'
        }}>
          <span style={{ color: '#dc2626' }}>⚠️ Warning:</span>
          <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#991b1b' }}>
            This action cannot be undone. This will permanently delete:
          </p>
          <ul style={{ margin: '8px 0 0 20px', fontSize: '12px', color: '#991b1b' }}>
            <li>The task "{task.title}"</li>
            <li>All remarks/comments on this task</li>
            <li>Task history and audit logs</li>
          </ul>
        </div>
        
        <label>Type <strong>"{task.title}"</strong> to confirm:</label>
        <input 
          type="text" 
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Enter task title to confirm"
          style={{ 
            width: '100%', 
            padding: '10px', 
            borderRadius: '8px', 
            border: '2px solid #e5e7eb',
            marginTop: '8px',
            marginBottom: '16px'
          }}
        />
        
        <div className="modal-actions" style={{ marginTop: '20px' }}>
          <button 
            onClick={handleDelete} 
            className="delete-btn" 
            disabled={isLoading || confirmText !== task.title}
            style={{
              flex: 1,
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '12px',
              borderRadius: '12px',
              fontWeight: '600',
              cursor: confirmText === task.title ? 'pointer' : 'not-allowed',
              opacity: confirmText === task.title ? 1 : 0.5
            }}
          >
            {isLoading ? 'Deleting...' : '🗑️ Delete Task'}
          </button>
          <button onClick={onClose} className="cancel-btn" style={{ flex: 1 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const RemarksModal = ({ remarks, onClose, taskTitle }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
      <h3>📝 Task Updates: {taskTitle}</h3>
      {remarks.length === 0 ? <p style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No updates yet</p> : (
        <div className="remarks-list">{remarks.map(remark => (
          <div key={remark.id} className="remark-item">
            <div className="remark-header"><strong>{remark.workerName}</strong><span className="remark-date">{remark.createdAtDate.toLocaleString()}</span></div>
            <p className="remark-text">{remark.remark}</p>
          </div>
        ))}</div>
      )}
      <div className="modal-actions"><button onClick={onClose} className="cancel-btn">Close</button></div>
    </div>
  </div>
);

const KanbanTask = ({ task, canEdit = false, currentUser, onTaskUpdate, onFilterByProject, projectProgress }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskRemarks, setTaskRemarks] = useState([]);
  const [loadingRemarks, setLoadingRemarks] = useState(false);
  const [remarksCount, setRemarksCount] = useState(0);
  
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: task.id,
    disabled: !canEdit || task.status === 'completed',
    data: { task }
  });

  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: 0.8 } : undefined;

  useEffect(() => {
    const fetchRemarksCount = async () => {
      try {
        const remarksQuery = query(collection(db, 'taskRemarks'), where('taskId', '==', task.id));
        const snapshot = await getDocs(remarksQuery);
        setRemarksCount(snapshot.size);
      } catch (error) { console.error(error); }
    };
    fetchRemarksCount();
  }, [task.id]);

  const fetchRemarks = async () => {
    setLoadingRemarks(true);
    try {
      const remarksQuery = query(collection(db, 'taskRemarks'), where('taskId', '==', task.id), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(remarksQuery);
      const remarks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), createdAtDate: doc.data().createdAt?.toDate?.() || new Date() }));
      setTaskRemarks(remarks);
      setShowRemarksModal(true);
    } catch (error) { console.error(error); alert('Failed to load remarks'); }
    finally { setLoadingRemarks(false); }
  };

  const getTaskProgress = () => {
    if (task.status === 'completed') return 100;
    if (task.status === 'ongoing') return 50;
    return 0;
  };
  const taskProgress = getTaskProgress();

  const handleStartTask = async () => {
    const confirmed = window.confirm(`Start working on "${task.title}"?`);
    if (!confirmed) return;
    setIsLoading(true);
    try {
      await updateDoc(doc(db, 'tasks', task.id), { status: 'ongoing', startedAt: new Date(), updatedAt: new Date() });
      if (task.assignedBy) {
        await addDoc(collection(db, 'notifications'), { userId: task.assignedBy, type: 'task_started', title: 'Task Started 🚀', message: `${currentUser?.displayName || 'A worker'} started "${task.title}"`, taskId: task.id, read: false, createdAt: new Date() });
      }
      onTaskUpdate?.();
    } catch (error) { console.error(error); alert('Failed to start task'); }
    finally { setIsLoading(false); }
  };

  const handleCompleteTask = async () => {
    const confirmed = window.confirm(`Mark "${task.title}" as complete? QC will be notified.`);
    if (!confirmed) return;
    setIsLoading(true);
    try {
      await updateDoc(doc(db, 'tasks', task.id), { status: 'completed', completedAt: new Date(), updatedAt: new Date(), qcVerified: false });
      const qcUsersQuery = query(collection(db, 'users'), where('role', '==', 'quality-control'));
      const qcSnapshot = await getDocs(qcUsersQuery);
      for (const qcDoc of qcSnapshot.docs) {
        await addDoc(collection(db, 'notifications'), { userId: qcDoc.id, type: 'task_completed', title: 'Task Ready for QC Verification ✅', message: `"${task.title}" has been completed and needs verification`, taskId: task.id, projectId: task.projectId, completedBy: currentUser?.uid, assignmentNote: task.assignmentNote || '', read: false, createdAt: new Date() });
      }
      onTaskUpdate?.();
    } catch (error) { console.error(error); alert('Failed to complete task'); }
    finally { setIsLoading(false); }
  };

  const handleReopenTask = async () => {
    const reason = prompt('Reason for reopening this task:');
    if (!reason) return;
    const confirmed = window.confirm(`Reopen "${task.title}"?`);
    if (!confirmed) return;
    setIsLoading(true);
    try {
      await updateDoc(doc(db, 'tasks', task.id), { status: 'ongoing', qcVerified: false, reopenedAt: new Date(), reopenedBy: currentUser.uid, reopenedReason: reason, updatedAt: new Date() });
      if (task.assignedTo) {
        const recipients = Array.isArray(task.assignedTo) ? task.assignedTo : [task.assignedTo];
        for (const recipientId of recipients) {
          await addDoc(collection(db, 'notifications'), { userId: recipientId, type: 'task_reopened', title: 'Task Reopened 🔄', message: `"${task.title}" has been reopened. Reason: ${reason}`, taskId: task.id, projectId: task.projectId, reopenedBy: currentUser.uid, reason: reason, read: false, createdAt: new Date() });
        }
      }
      onTaskUpdate?.();
      alert('✅ Task has been reopened and moved to Ongoing');
    } catch (error) { console.error(error); alert('Failed to reopen task'); }
    finally { setIsLoading(false); }
  };

  const handleDeleteTask = async () => {
    // This will be called after confirmation in the delete modal
    onTaskUpdate?.();
  };

  const formatDuration = () => {
    if (task.estimatedDays) return `${task.estimatedDays} day${task.estimatedDays !== 1 ? 's' : ''}`;
    if (task.startDate && task.endDate) {
      const start = task.startDate.toDate ? task.startDate.toDate() : new Date(task.startDate);
      const end = task.endDate.toDate ? task.endDate.toDate() : new Date(task.endDate);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
    return 'No date set';
  };
  
  const getFormattedDate = (date) => {
    if (!date) return null;
    
    let dateObj;
    if (date.toDate) {
      dateObj = date.toDate();
    } else {
      dateObj = new Date(date);
    }
    
    if (isNaN(dateObj.getTime())) return null;
    
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayName = days[dateObj.getDay()];
    const day = dateObj.getDate();
    let ordinal;
    if (day === 1 || day === 21 || day === 31) ordinal = 'st';
    else if (day === 2 || day === 22) ordinal = 'nd';
    else if (day === 3 || day === 23) ordinal = 'rd';
    else ordinal = 'th';
    const monthName = months[dateObj.getMonth()];
    const year = dateObj.getFullYear();
    
    return `${dayName}, ${day}${ordinal} ${monthName} ${year}`;
  };

  const getStatusBadge = () => {
    switch(task.status) {
      case 'pending': return { bg: '#fef3c7', color: '#92400e', text: 'Pending' };
      case 'ongoing': return { bg: '#dbeafe', color: '#1e40af', text: 'In Progress' };
      case 'completed': return { bg: '#d1fae5', color: '#065f46', text: 'Completed' };
      default: return { bg: '#f3f4f6', color: '#374151', text: task.status };
    }
  };

  const statusBadge = getStatusBadge();
  const isAssigned = task.assignedTo && (Array.isArray(task.assignedTo) ? task.assignedTo.length > 0 : true);
  const canStart = task.status === 'pending' && isAssigned && (Array.isArray(task.assignedTo) ? task.assignedTo.includes(currentUser?.uid) : task.assignedTo === currentUser?.uid);
  const canComplete = task.status === 'ongoing' && (Array.isArray(task.assignedTo) ? task.assignedTo.includes(currentUser?.uid) : task.assignedTo === currentUser?.uid);
  const isWorkerView = !canEdit;
  const canReopen = canEdit && task.status === 'completed' && task.qcVerified;

  let dueDate = null;
  if (task.deadline) dueDate = task.deadline.toDate ? task.deadline.toDate() : new Date(task.deadline);
  else if (task.endDate) dueDate = task.endDate.toDate ? task.endDate.toDate() : new Date(task.endDate);
  if (dueDate) dueDate.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isOverdue = dueDate && dueDate < today && task.status !== 'completed';

  const handleProjectClick = (e) => {
    e.stopPropagation();
    if (onFilterByProject && task.projectId) onFilterByProject(task.projectId, task.projectName);
  };

  const getAssignedNames = () => {
    if (Array.isArray(task.assignedToNames)) return task.assignedToNames.join(', ');
    if (task.assignedToName) return task.assignedToName;
    if (task.assignedTo && !Array.isArray(task.assignedTo)) return task.assignedTo;
    return 'Unknown';
  };

  const getWorkerCount = () => {
    if (Array.isArray(task.assignedTo)) return task.assignedTo.length;
    return task.assignedTo ? 1 : 0;
  };

  return (
    <>
      <div ref={setNodeRef} style={style} className={`task-card ${isOverdue ? 'overdue' : task.status}`}>
        {isOverdue && <div className="overdue-flag">🔴 OVERDUE</div>}
        
        {/* Top row with project badge and view updates button */}
        <div className="task-top-row">
          <div className={`task-project-badge ${onFilterByProject ? 'clickable' : ''}`} onClick={handleProjectClick} title={onFilterByProject ? "Click to filter by this project" : ""}>
            📁 {task.projectName}{onFilterByProject && <span className="filter-icon">🔍</span>}
          </div>
          {canEdit && (
            <button onClick={fetchRemarks} className="view-remarks-button" title="View task updates">
              📝 {remarksCount > 0 && <span className="remarks-badge">{remarksCount}</span>}
            </button>
          )}
        </div>
        
        {canEdit && task.status !== 'completed' && (
          <div className="drag-handle-bottom" {...listeners} {...attributes} title="Drag to assign this task to a worker">
            <span className="drag-icon">🎯</span><span className="drag-text">DRAG TO ASSIGN</span>
          </div>
        )}
        
        <div className="task-status-badge" style={{ background: statusBadge.bg, color: statusBadge.color }}>{statusBadge.text}</div>
        
        {isAssigned && (
          <div className="task-assigned-badge">
            👥 Assigned to: <strong>{getAssignedNames()}</strong>
            {getWorkerCount() > 1 && <span className="multi-worker-badge">{getWorkerCount()} workers</span>}
          </div>
        )}
        
        {/* Task Title with Edit and Delete Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
          <h4 className="task-title" style={{ margin: 0, flex: 1 }}>{task.title}</h4>
          {canEdit && (
            <div style={{ display: 'flex', gap: '6px' }}>
              <button 
                onClick={() => setShowEditModal(true)} 
                className="edit-task-btn" 
                title="Edit task title and description"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: 'white',
                  border: 'none',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                ✏️ Edit
              </button>
              <button 
                onClick={() => setShowDeleteModal(true)} 
                className="delete-task-btn" 
                title="Delete task"
                style={{
                  background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                  color: 'white',
                  border: 'none',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                🗑️ Delete
              </button>
            </div>
          )}
        </div>
        
        {task.description && <p className="task-description">{task.description}</p>}
        
        <div className="progress-section">
          <div className="progress-label"><span>📋 Task Progress</span><span>{taskProgress}%</span></div>
          <div className="progress-bar-container"><div className="progress-bar-fill" style={{ width: `${taskProgress}%`, background: taskProgress === 100 ? '#10b981' : taskProgress >= 50 ? '#f59e0b' : '#3b82f6' }}></div></div>
        </div>

        {projectProgress !== undefined && (
          <div className="progress-section">
            <div className="progress-label"><span>📁 Project Progress</span><span>{projectProgress}%</span></div>
            <div className="progress-bar-container"><div className="progress-bar-fill" style={{ width: `${projectProgress}%`, background: 'var(--maroon-primary)' }}></div></div>
          </div>
        )}
        
        <div className="task-meta-grid">
          {task.category && <div className="task-meta-item"><span className="meta-icon">🏷️</span><span>{task.category}</span></div>}
          <div className="task-meta-item"><span className="meta-icon">⏱️</span><span>{formatDuration()}</span></div>
          {task.startDate && <div className="task-meta-item"><span className="meta-icon">🚀 Start</span><span>{getFormattedDate(task.startDate)}</span></div>}
          {task.endDate && <div className={`task-meta-item ${isOverdue ? 'overdue' : ''}`}><span className="meta-icon">🏁 End</span><span>{getFormattedDate(task.endDate)}</span></div>}
        </div>
        
        <div className="task-actions">
          {isWorkerView && !canEdit && (
            <>
              {canStart && <button onClick={handleStartTask} className="task-btn start-btn" disabled={isLoading}>{isLoading ? '...' : '🚀 Start Task'}</button>}
              {canComplete && <button onClick={handleCompleteTask} className="task-btn complete-btn" disabled={isLoading}>{isLoading ? '...' : '✅ Mark Complete'}</button>}
            </>
          )}
          {canReopen && <button onClick={handleReopenTask} className="task-btn reopen-btn" disabled={isLoading}>{isLoading ? '...' : '🔄 Reopen Task'}</button>}
          {canEdit && <button onClick={() => setShowExtendModal(true)} className="task-btn extend-btn">⏰ Extend Deadline</button>}
        </div>
        
        {task.status === 'completed' && task.qcVerified && <div className="task-verified-badge">✓ QC Verified</div>}
        {task.status === 'completed' && !task.qcVerified && <div className="task-pending-qc-badge">⏳ Awaiting QC Verification</div>}
        
        {canEdit && task.status !== 'completed' && !isAssigned && <div className="drag-indicator">⋮⋮ Drag to assign</div>}
        {canEdit && isAssigned && task.status === 'pending' && <div className="drag-indicator assigned">✓ Assigned to {getWorkerCount()} worker{getWorkerCount() !== 1 ? 's' : ''}</div>}
      </div>

      {showRemarksModal && <RemarksModal remarks={taskRemarks} taskTitle={task.title} onClose={() => setShowRemarksModal(false)} />}
      {showExtendModal && <ExtendTaskDateModal task={task} currentUser={currentUser} onClose={() => setShowExtendModal(false)} onSuccess={onTaskUpdate} />}
      {showEditModal && (
        <EditTaskModal 
          task={task} 
          currentUser={currentUser} 
          onClose={() => setShowEditModal(false)} 
          onSave={() => {
            onTaskUpdate?.();
            setShowEditModal(false);
          }} 
        />
      )}
      {showDeleteModal && (
        <DeleteTaskModal 
          task={task} 
          currentUser={currentUser} 
          onClose={() => setShowDeleteModal(false)} 
          onDelete={() => {
            onTaskUpdate?.();
            setShowDeleteModal(false);
          }} 
        />
      )}
    </>
  );
};

export default KanbanTask;